import React, { useCallback, useEffect, useRef, useState } from 'react';
import { gql, useMutation } from '@apollo/client';

import { LoadingSpinner } from '@trqk/components';
import { locale } from '@trqk/utils';

import { PaymentPlanContents } from './components';
import { plansWrapperHeight } from './constants';
import { getQrrOverageMessage } from './utils';
import {
    useToast,
    useUploadStatements,
    usePaymentPlanStatus,
} from '../../../../../hooks';
import { EXTERNAL_REDIRECTS } from '../../../../../constants';

const VIEWED_PRICING = gql`
    mutation viewedPricing($user_id: String) {
        viewedPricing(user_id: $user_id)
    }
`;

interface PaymentPlanProps {
    userId: Maybe<string>;
}

/**
 * Allows the user to adjust their current payment plan or change to a different payment plan.
 */
export const PaymentPlan = ({
    userId,
}: PaymentPlanProps): JSX.Element | null => {
    const { add: addToast } = useToast();

    const {
        chargebeePlanSettings,
        fetchChargebeeSubscriptionSettings,
        fetchTrqkUserSubscriptionSettings,
        subscriptionSettings,
        usage,
    } = usePaymentPlanStatus();

    useEffect(() => {
        if (userId) {
            fetchTrqkUserSubscriptionSettings();
        }
    }, [userId]);

    const [viewedPricing] = useMutation(VIEWED_PRICING);
    const didCallViewedPricingRef = useRef(false);

    const onButtonViewed = useCallback((): void => {
        if (!didCallViewedPricingRef.current && userId !== null) {
            viewedPricing({
                variables: {
                    user_id: userId,
                },
            });

            didCallViewedPricingRef.current = true;
        }
    }, [didCallViewedPricingRef.current, userId]);

    const redirectUrl = `${window.location.protocol}//${window.location.host}${EXTERNAL_REDIRECTS.PAYMENT_PLAN}`;

    const { failedIngestions } = useUploadStatements();
    const qrrOverageMessage = getQrrOverageMessage({
        failedIngestions,
        subscriptionSettings,
    });

    const [chargebeeInstance, setChargebeeInstance] = useState<
        null | ChargebeeInstance | undefined
    >();

    // Get a reference to the Chargebee instance
    useEffect(() => {
        if (chargebeeInstance === undefined) {
            const Chargebee: ChargebeeJS | null =
                // eslint-disable-next-line -- the global Chargebee object is loaded in packages/web/public/index.html
                // @ts-ignore
                window?.Chargebee ?? window?.parent?.window?.Chargebee ?? null;

            if (Chargebee) {
                const instance = Chargebee.getInstance();

                setChargebeeInstance(instance);
            } else {
                // eslint-disable-next-line no-console
                console.log('Unable to load window.Chargebee');
                setChargebeeInstance(null);
            }
        }
    }, [chargebeeInstance]);

    // If chargebeeInstance can't be found, show a loading spinner
    if (chargebeeInstance === null || chargebeeInstance === undefined) {
        return (
            <div
                className="px-12 py-10 bg-white rounded-lg"
                style={{ height: plansWrapperHeight }}
            >
                <div className="flex justify-between">
                    <h2 className="text-xl">
                        {locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.TITLE}
                    </h2>
                </div>
                <div className="h-full w-full flex justify-center items-center">
                    <LoadingSpinner />
                </div>
            </div>
        );
    }

    const openCheckoutOptions = {
        error: () => {
            addToast({
                kind: 'warning-banner',
                content:
                    locale.enUS.ACCOUNT.SECTIONS.PAYMENT_PLAN.TOASTS
                        .PROBLEM_DURING_CHECKOUT,
            });
        },
        success: () => {
            // If Chargebee tells us that the checkout succeeded, we refetch
            // getChargebeeSubscriptionSettings to see if their subscription details have changed
            if (userId) {
                fetchChargebeeSubscriptionSettings(userId);

                if (chargebeeInstance) {
                    // Close the checkout modal automatically when checkout succeeds. Without this
                    // call, the user has to click a "CLOSE" button at the end of checkout.
                    chargebeeInstance.closeAll();
                }
            }
        },
    };

    return (
        <PaymentPlanContents
            chargebeeInstance={chargebeeInstance}
            chargebeePlanSettings={chargebeePlanSettings}
            onSubscriptionChanged={() => {
                if (userId !== null) {
                    fetchChargebeeSubscriptionSettings(userId);
                }
            }}
            onButtonViewed={onButtonViewed}
            openCheckoutOptions={openCheckoutOptions}
            qrrOverageMessage={qrrOverageMessage}
            redirectUrl={redirectUrl}
            subscriptionSettings={subscriptionSettings}
            usage={usage}
        />
    );
};
